import React from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";

const NAV_LINKS = [
  { label: "Home", to: "/", tid: "footer-nav-home" },
  { label: "Browse", to: "/browse", tid: "footer-nav-browse" },
  { label: "CSR Family Support Hub", to: "/csr", tid: "footer-nav-csr" },
  { label: "How It Works", to: "/info/how-it-works", tid: "footer-nav-how-it-works" },
  { label: "🎬 Explainer Videos", to: "/videos", tid: "footer-nav-videos" },
  { label: "Start Up Guide & FAQ", to: "/parent-help", tid: "footer-nav-parent-help" },
  { label: "About Us", to: "/info/about", tid: "footer-nav-about" },
  { label: "Waitlist", to: "/?waitlist=1", tid: "footer-nav-waitlist" },
];

const LEGAL_LINKS = [
  { label: "Privacy Policy", to: "/info/privacy", tid: "footer-legal-privacy" },
  { label: "Terms & Conditions", to: "/info/terms", tid: "footer-legal-terms" },
  { label: "Parental Safety Guidelines", to: "/info/safety", tid: "footer-legal-safety" },
  { label: "PTA & Charity Partner Terms", to: "/legal/partner-agreement", tid: "footer-legal-partner-agreement" },
  { label: "Information Hub", to: "/info", tid: "footer-legal-info-hub" },
];

export default function Footer() {
  return (
    <footer className="mt-24 border-t border-slate-100 bg-white/60" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 grid gap-10 md:grid-cols-4 text-sm text-slate-600">
        {/* Brand column */}
        <div className="md:col-span-1">
          <Link to="/" className="font-display text-xl font-semibold" data-testid="footer-brand">
            <span className="text-brandPink">My</span><span className="text-brandTeal">TableTop</span><span className="text-brandSlate">App</span>
          </Link>
          <p className="mt-3 max-w-xs leading-relaxed">
            A Hackney Born, parent-approved marketplace where toddlers to teens learn to earn, sell, and save — safely.
          </p>
          {/* Social icons intentionally hidden until we have live URLs — see PRD */}
        </div>

        {/* Navigation */}
        <div>
          <div className="font-bold text-brandSlate mb-3 tracking-wide uppercase text-xs">Explore</div>
          <ul className="space-y-2" data-testid="footer-nav-list">
            {NAV_LINKS.map((l) => (
              <li key={l.tid}>
                <Link
                  to={l.to}
                  className="hover:text-brandPink transition"
                  data-testid={l.tid}
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Legal */}
        <div>
          <div className="font-bold text-brandSlate mb-3 tracking-wide uppercase text-xs">Legal & Safety</div>
          <ul className="space-y-2" data-testid="footer-legal-list">
            {LEGAL_LINKS.map((l) => (
              <li key={l.tid}>
                <Link
                  to={l.to}
                  className="hover:text-brandTeal transition"
                  data-testid={l.tid}
                >
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <button
                type="button"
                onClick={() => {
                  try { window.dispatchEvent(new CustomEvent("mtt:cookie-consent-reopen")); } catch (_) { /* noop */ }
                }}
                className="hover:text-brandTeal transition text-left"
                data-testid="footer-legal-cookie-prefs"
              >
                Cookie preferences
              </button>
            </li>
          </ul>
        </div>

        {/* Contact */}
        <div>
          <div className="font-bold text-brandSlate mb-3 tracking-wide uppercase text-xs">Contact</div>
          {/* Iteration 129 — raw email + mailto replaced with a clean
              in-app route to the Contact Us form so the support inbox
              stays private and every enquiry flows through the
              moderated form pipeline. */}
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 text-brandTeal font-semibold hover:text-brandTealDark transition"
            data-testid="footer-contact-link"
            onClick={(e) => {
              // Iteration 302b — already ON the contact page? Skip the
              // route change and smooth-scroll straight to the form top
              // (cross-page clicks are handled by Contact's mount scroll,
              // which retries so it survives tablet layout shifts).
              if (window.location.pathname === "/contact") {
                e.preventDefault();
                const go = () => document.getElementById("contact-heading")?.scrollIntoView({ behavior: "smooth", block: "start" });
                go();
                setTimeout(go, 400);
              }
            }}
          >
            <Mail className="w-4 h-4 flex-shrink-0" /> Contact Us
          </Link>
          <p className="mt-3 text-xs leading-relaxed">
            Questions, safety concerns, or press enquiries — we reply within 24 hours.
          </p>
        </div>
      </div>

      <div className="border-t border-slate-100 py-4 text-center text-xs text-slate-500">
        © {new Date().getFullYear()} MyTableTopApp — all rights reserved.
      </div>
    </footer>
  );
}
